import { useState, useEffect } from 'react';

/**
* Tracks visibility of a referenced element using an IntersectionObserver and returns whether it is visible.
* @example
* useIntersectionObserver(ref, options)
* true
* @param {{React.MutableRefObject<any>}} {{ref}} - Ref of the element to observe for intersection.
* @param {{IntersectionObserverInit}} {{options}} - Optional IntersectionObserver configuration overrides.
* @returns {{boolean}} Indicates if the referenced element has become visible.
**/
const useIntersectionObserver = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1,
      ...options
    });
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [ref, options]);
  
  return isVisible;
};

export default useIntersectionObserver;
